'use client'

import React, { useCallback, useState } from 'react'
import { useTranslations } from '@/lib/next-shim'
import FileSaver from 'file-saver'

import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Slide from '@mui/material/Slide'
import Tooltip from '@mui/material/Tooltip'
import { TransitionProps } from '@mui/material/transitions'

import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import FilePreview, { FilePreviewProps } from '@/shared/ui/file-preview/FilePreview'

export type FilePreviewDialogProps = {
  disableChangeFileName?: boolean
  /**
   * file name
   */
  fileName: string
  /**
   * file type
   */
  fileType: string
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void
  /**
   * If `true`, the component is shown
   */
  open: boolean
  PhotoViewerProps?: FilePreviewProps['PhotoViewerProps']
  /**
   * file source
   */
  src: string
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export function FilePreviewDialog(props: FilePreviewDialogProps) {
  const { disableChangeFileName = false, fileName, fileType, onClose, open, PhotoViewerProps, src } = props
  const t = useTranslations()
  const [downloading, setDownloading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)

  const handleClose = () => {
    onClose()
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const response = await fetch(src)
      if (response.ok && response.status === 200) {
        const blob = await response.blob()
        FileSaver.saveAs(blob, `${fileName}`)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setDownloading(false)
    }
  }

  const handleLoadFileError = useCallback(() => {
    setHasError(true)
  }, [])

  const handleOpenNewWindow = () => {
    window.open(src)
  }

  return (
    <Dialog
      data-testid="file-preview-dialog"
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
    >
      {downloading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: (theme) => theme.zIndex.appBar + 1,
            color: (theme) => theme.palette.common.white,
          }}
        >
          <LinearProgress color="inherit" />
        </Box>
      )}
      <AppBar position="fixed">
        <Toolbar>
          <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Tooltip title={t('button.download.tooltip')}>
                <span>
                  <IconButton color="inherit" disabled={hasError} onClick={handleDownload}>
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('button.openInNewTab.tooltip')}>
                <span>
                  <IconButton color="inherit" disabled={hasError} onClick={handleOpenNewWindow}>
                    <OpenInNewIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('button.close.tooltip')}>
                <IconButton color="inherit" onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center' }}>
        <FilePreview fileName={fileName} fileType={fileType} filePath={src} onError={handleLoadFileError} PhotoViewerProps={PhotoViewerProps} />
      </Box>
    </Dialog>
  )
}
